import React from "react";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaLightbulb,
  FaSearch,
  FaBullhorn,
  FaShareAlt,
  FaEnvelopeOpenText,
  FaChartLine,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Services.css";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const Services = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: "digital-strategy",
      tag: "Plan",
      title: "Strategy & consulting",
      desc: "Goals, audience, budget, and channel priorities aligned to your business. Delivered as workshops, audits, or retainers—scoped in writing.",
      icon: <FaLightbulb />,
      bullets: ["Market & competitor review", "KPI definitions", "Roadmaps & pilots"],
    },
    {
      id: "seo-content",
      tag: "Organic",
      title: "SEO & content marketing",
      desc: "Technical hygiene, keyword research, content briefs, and on-page structure. We do not guarantee rankings; search engines control outcomes.",
      icon: <FaSearch />,
      bullets: ["Site audits", "Content calendars", "Internal linking plans"],
    },
    {
      id: "paid-media",
      tag: "Paid",
      title: "Paid search & social",
      desc: "Google Ads, Meta, LinkedIn, and other platforms where it fits your offer. Conversion tracking, creative testing, and budget guardrails.",
      icon: <FaBullhorn />,
      bullets: ["Campaign build & QA", "Audience & bid strategy", "Ongoing optimization"],
    },
    {
      id: "social-media",
      tag: "Social",
      title: "Social media management",
      desc: "Editorial calendars, community guidelines, creative coordination, and reporting—aligned with brand and compliance needs.",
      icon: <FaShareAlt />,
      bullets: ["Organic + paid alignment", "UGC & rights reminders", "Crisis-aware tone"],
    },
    {
      id: "email-marketing",
      tag: "CRM",
      title: "Email & lifecycle",
      desc: "List hygiene, segmentation, automation flows, and CAN-SPAM/GDPR-aware practices (consult counsel for regulated industries).",
      icon: <FaEnvelopeOpenText />,
      bullets: ["Welcome & nurture", "Promotions & newsletters", "Deliverability checks"],
    },
    {
      id: "analytics-cro",
      tag: "Measure",
      title: "Analytics & CRO",
      desc: "GA4, tag manager, dashboards, and landing-page tests. We improve clarity and conversion paths—we do not promise lift percentages.",
      icon: <FaChartLine />,
      bullets: ["Event mapping", "Funnel reporting", "A/B test planning"],
    },
  ];

  return (
    <section className="srv2" aria-label="Services">
      <div className="srv2__wrap">
        <motion.div
          className="srv2__head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <div>
            <div className="srv2__badge">
              <span className="dot" aria-hidden="true" />
              Service catalog
            </div>

            <h2 className="srv2__title">
              Digital marketing built for <span>clarity</span>.
            </h2>

            <p className="srv2__sub">
              Pick one service or combine several. Each card opens a detailed overview. Final scope, fees, and timelines
              are confirmed in a proposal or statement of work—not on this marketing site alone.
            </p>
          </div>

          <div className="srv2__aside">
            <b>Affiliate &amp; referrals:</b>
            <p>
              If we recommend tools or earn referral fees, we disclose that relationship where required. That does not
              change our obligation to give honest advice.
            </p>
          </div>
        </motion.div>

        <div className="srv2__grid">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              className="srv2__card"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: i * 0.05 }}
              onClick={() => navigate(`/services/${s.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/services/${s.id}`)}
            >
              <div className="srv2__top">
                <div className="srv2__icon" aria-hidden="true">
                  {s.icon}
                </div>
                <div className="srv2__tag">{s.tag}</div>
              </div>

              <h3 className="srv2__h3">{s.title}</h3>
              <p className="srv2__p">{s.desc}</p>

              <ul className="srv2__bullets">
                {s.bullets.map((b, idx) => (
                  <li key={idx}>{b}</li>
                ))}
              </ul>

              <span className="srv2__link">
                Service details <FaArrowRight aria-hidden />
              </span>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="srv2__note"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={fadeUp}
        >
          <p>
            <strong>Results vary.</strong> Performance depends on your offer, budget, competition, creative, tracking
            quality, and platform policies. We do not claim fixed ROI, impression share, or ranking positions.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
